import dynamic from "next/dynamic"
import { useEffect, useState } from "react"
import { Route, Navigate, Routes } from "react-router-dom"
import { toast } from "react-toastify"

import { PrivateRoute } from "./PrivateRoute"

import { Analytics, FunnelDocs } from "utils/analytics"
import { ClientAccountRouter } from "modules/ClientAccount"
import { FreePsysPage } from "pages/FreePsysPage"
import { FriendlyLabelsStorage } from "components/Labels"
import { NotFoundPage } from "pages/NotFoundPage"
import { Payment } from "modules/Payment"
import { PsychologistsCollection } from "modules/PsychologistsCollection"
import { NewClientSurvey } from "modules/NewClientSurvey"
import { useUpdateSearchParams } from "app/AppRouter/hooks/useUpdateSearchParams"
import { getCurrentStatus } from "app/AppRouter/utils"
import { Layout, LayoutWithSpinner } from "components"
import { PsyProfileClient } from "modules/PsyProfile/PsyProfileClient"
import { BookingDetailsWrapper } from "modules/PsychologistAccount/BookingDetails/BookingDetailsWrapper"
import { StepThreeResults } from "modules/NewClientSurvey/components/StepThreeResults"
import { CATALOG_PATH } from "modules/PsyCatalog/common/utils/catalogPath"
import { PsyCatalogPage } from "modules/PsyCatalog/browser-side"
import { BookingWrapper } from "modules/Booking/BookingWrapper"
import { useDidMount, useSearchParams } from "utils/customHooks"
import { UserAccount } from "utils/UserAccount"
import { IS_DEVELOPMENT, IS_PRODUCTION } from "utils/env-management"
import { APP_STATUSES } from "utils/AppStatusContext"
import { PsychologistListPage } from "pages/PsychologistListPage"
import { ChatPage } from "pages"
import { ChatSocketWrapper } from "modules/Chat/components"
import { ClientCompanyAuthorizationPage } from "modules/ClientAuthorization/Company"
import { ClientAuthorizationPage } from "modules/ClientAuthorization/Default"
import { ROLES } from "utils/constants"

UserAccount.executeOnceWhenSignedIn(() => Analytics.gadv_client_signup())

const PsychologistAccountLoader = dynamic(
    () =>
        import("modules/PsychologistAccount/PsychologistAccountPage").then(
            (m) => m.PsychologistAccountPage
        ),
    {
        loading: () => <LayoutWithSpinner />,
        ssr: false,
    }
)

const ClientCallAppLoader = dynamic(() => import("modules/Call").then((m) => m.ClientCallApp), {
    loading: () => <LayoutWithSpinner />,
    ssr: false,
})

const InternalCallAppLoader = dynamic(() => import("modules/Call").then((m) => m.InternalCallApp), {
    loading: () => <LayoutWithSpinner />,
    ssr: false,
})

const ClientCardAttachmentLoader = dynamic(
    () => import("components/ClientCardAttachment").then((m) => m.ClientCardAttachment),
    {
        loading: () => <LayoutWithSpinner />,
        ssr: false,
    }
)

export function AppRouter() {
    const [error, setError] = useState(false)
    const currentStatus = getCurrentStatus()
    const searchParams = useSearchParams()

    const removeFrom = useUpdateSearchParams((params) => {
        params.delete("from")
        return params
    })

    const onError = () => {
        setError(true)
    }

    useDidMount(() => {
        FriendlyLabelsStorage.init(window.document.referrer)
    })

    useEffect(() => {
        toast.dismiss()
        currentStatus && currentStatus in Analytics && Analytics[currentStatus]()
        if (searchParams.get("from") === APP_STATUSES.client_authorization) {
            toast("Вы авторизованы")
            if (
                currentStatus === APP_STATUSES.client_account &&
                searchParams.get("from") === APP_STATUSES.client_authorization
            ) {
                Analytics.lk_login_success()
                removeFrom()
            }
        }
    }, [currentStatus])

    if (error) {
        return <NotFoundPage />
    }

    return (
        <Routes>
            <Route path="/psychologist/collection/:id" element={<PsychologistsCollection />} />
            <Route path="/psychologist/list" element={<PsychologistListPage />} />
            <Route path="/psychologist/:id" element={<PsyProfileClient onError={onError} />} />
            <Route
                path="/psychologists/:filter/:slug"
                element={<PsyProfileClient onError={onError} />}
            />
            <Route path="/psychologists/free/booking" element={<FreePsysPage />} />
            <Route path="/psychologists/free" element={<FreePsysPage />} />
            <Route path="/psychologists" element={<PsyCatalogPage />} />
            <Route path={CATALOG_PATH.online} element={<PsyCatalogPage />} />
            <Route path={CATALOG_PATH.anyPageOnline} element={<PsyCatalogPage />} />
            <Route path={CATALOG_PATH.anyPage} element={<PsyCatalogPage />} />
            <Route path={CATALOG_PATH.anyPageFilter} element={<PsyCatalogPage />} />
            <Route path={CATALOG_PATH.anyFilter} element={<PsyCatalogPage />} />
            <Route path="/booking/:id" element={<BookingDetailsWrapper />} />
            <Route path="/booking" element={<BookingWrapper />} />
            <Route path="/payment/:id" element={<Payment />} />
            <Route path="/psychologist-account/*" element={<PsychologistAccountLoader />} />
            <Route path="/my/authorization" element={<ClientAuthorizationPage />} />
            <Route path="/my/company/authorization" element={<ClientAuthorizationPage />} />
            <Route
                path="/my/company/authorization/:id"
                element={<ClientCompanyAuthorizationPage />}
            />
            <Route path="/my/call/*" element={<ClientCallAppLoader />} />
            <Route
                path="/my/*"
                element={
                    <PrivateRoute roles={[ROLES.CLIENT]}>
                        <ClientAccountRouter />
                    </PrivateRoute>
                }
            />
            <Route path="/funnel-docs" element={<FunnelDocs />} />
            {IS_DEVELOPMENT && (
                <Route path="/ssg/psychologists/:filter/:page" element={<PsyCatalogPage />} />
            )}
            {IS_DEVELOPMENT && (
                <Route path="/ssg/psychologists/:filter" element={<PsyCatalogPage />} />
            )}
            {!IS_PRODUCTION && (
                <Route path="/my/card-attachment" element={<ClientCardAttachmentLoader />} />
            )}
            {!IS_PRODUCTION && (
                <Route path="/internal/call/*" element={<InternalCallAppLoader />} />
            )}
            <Route path="/survey/psy/result" element={<StepThreeResults />} />
            <Route
                path="/survey/psy"
                element={
                    <Layout>
                        <NewClientSurvey />
                    </Layout>
                }
            />
            <Route path="*" element={<NotFoundPage />} />
            {/* Redirects */}
            <Route path="/" element={<Navigate to="/survey/psy" replace />} />
            <Route path="/client-survey" element={<Navigate to="/survey/psy" replace />} />
            <Route
                path="/chat"
                element={
                    <PrivateRoute roles={[ROLES.CLIENT, ROLES.PSY]}>
                        <ChatSocketWrapper>
                            <ChatPage />
                        </ChatSocketWrapper>
                    </PrivateRoute>
                }
            />
            <Route
                path="/chat/:chatId"
                element={
                    <PrivateRoute roles={[ROLES.CLIENT, ROLES.PSY]}>
                        <ChatSocketWrapper>
                            <ChatPage />
                        </ChatSocketWrapper>
                    </PrivateRoute>
                }
            />
        </Routes>
    )
}
